import React from 'react';
import PropTypes from 'prop-types';
import CSSTransition from 'react-transition-group/CSSTransition';
import { MDText } from 'i18n-react';

import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import modalCloseSvg from '@adsk/bim360-matrix-react-assets/modal-close.svg';

import './_IFrameModal.scss';

import { getTitle } from './helpers';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import isTrustedUrl from '@adsk/bim360-matrix-react-helpers/es/isTrustedURL';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';

export class IFrameModal extends React.PureComponent {
  closeViewer = () => {
    const { iframeSourceUrlParameter, iframeTitleUrlParameter, router, location } = this.props;
    if (location) {
      const query = { ...location.query };

      delete query[iframeSourceUrlParameter];
      delete query[iframeTitleUrlParameter];

      router.push({ pathname: router.location.pathname, query });
    }
  };

  renderHeader = title => {
    const { hideHeader, headerRenderer } = this.props;

    if (hideHeader) return null;

    if (typeof headerRenderer === 'function') {
      return headerRenderer();
    } else if (headerRenderer) {
      return headerRenderer;
    }

    return (
      <header className="IFrameModal__header">
        <div className="IFrameModal__title" data-testid="IFrameModal__title">
          {getTitle(title)}
        </div>
        <IconButton
          onClick={this.closeViewer}
          className="IFrameModal__close"
          data-testid="IFrameModal__close"
        >
          <SvgIcon width="13" height="13">
            {modalCloseSvg}
          </SvgIcon>
        </IconButton>
      </header>
    );
  };

  renderUntrustedURL = () => {
    return (
      <EmptyState
        icon={EmptyState.Icons.CONE}
        text={this.props[TSymbolProp].translate('URL_ERROR')}
        className="IFrameModal__untrusted-url-empty-state"
      />
    );
  };

  renderBody = (url, whitelist) => {
    return isTrustedUrl(url, whitelist) ? (
      <iframe src={url} className="IFrameModal__iframe" data-testid="IFrameModal__iframe" />
    ) : (
      this.renderUntrustedURL()
    );
  };

  render() {
    const { iframeSourceUrlParameter, iframeTitleUrlParameter, location } = this.props;

    const innerIframeUrl = location && location.query && location.query[iframeSourceUrlParameter];
    const innerTitleUrl = location && location.query && location.query[iframeTitleUrlParameter];
    const showViewer = !!innerIframeUrl;

    return (
      <CSSTransition
        key={1}
        in={showViewer}
        classNames="IFrameModal--animation"
        mountOnEnter={true}
        unmountOnExit={true}
        timeout={{ enter: 200, leave: 150 }}
      >
        <div className="IFrameModal">
          {this.renderHeader(innerTitleUrl)}
          {this.renderBody(innerIframeUrl, this.props.whitelist)}
        </div>
      </CSSTransition>
    );
  }
}

IFrameModal.propTypes = {
  /**
   * Pass the loation from react-router
   */
  location: PropTypes.object.isRequired,
  /**
   * Pass the router from react-router
   */
  router: PropTypes.object.isRequired,
  /**
   * The name of the URL query param that holds the URL that the iFrame should use. IFrameModal looks for the existence of this parameter in the URL and then appears/hides accordingly.
   * This way we achieve back/forward/deep linking functionality
   */
  iframeSourceUrlParameter: PropTypes.string.isRequired,
  /**
   * Optional - The name of the URL query param that holds the title of the iFrame document to show in the modal header. IFrameModal looks for the existence of this parameter in the URL and uses its value.
   */
  iframeTitleUrlParameter: PropTypes.string,
  /**
   * A list of optional whitelisted domains as either strings, regexps or functions to match the url against.
   * The function variant of the whitelist is of the form: ({ hostname }) => bool.
   *
   * Note: "*.autodesk.com" will always be matched so no need to specify it in the whitelist.
   */
  whitelist: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp), PropTypes.func])
    ),
    PropTypes.string,
    PropTypes.instanceOf(RegExp),
    PropTypes.func
  ]),
  /**
   * Control the visibility of the header.
   */
  hideHeader: PropTypes.bool,
  /**
   * Render a custom header.
   */
  headerRenderer: PropTypes.func
};

export default withLocale(
  IFrameModal,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
